import {
    employeeConfirm,
    getEmployeeInfoByTokenConfirm,
} from '@/modules/Employee/services'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Loading } from 'vnetwork-ui'
import { EmployeeConfimationValues } from '../../types'
import useUploadFile from '@/hooks/useUploadFile'
import { Attachment } from '@/modules/Upload/types'
import AddressTab from './AddressTab'
import { defaultValues } from './defaultValues'
import ExperienceTab from './ExpTab'
import FinanceTab from './FinanceTab'
import IdentificationTab from './IdentificationTab'
import PersonalTab from './PersonalTab'
import ProfileHeader from './ProfileHeader'
import ProfileTab from './ProfileTab'
import { validateStep } from './schema'
import { yupResolver } from '@hookform/resolvers/yup'
import CheckIcons from '@/icons/CheckIcons'
import { formatPGDate } from '@/utils/function'
import OnboardingSuccess from './OnboardingSuccess'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import RegulationsGate from './RegulationsGate'

type TabType =
    | 'Personal'
    | 'Identification'
    | 'Experience'
    | 'Profile'
    | 'Address'
    | 'Finance'

const EmployeeConfirmationNavbar: React.FC = () => {
    return (
        <nav className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
            <div className="max-w-full mx-auto px-4 py-3">
                <img
                    src="/images/icon.png"
                    alt="VNETWORK"
                    className="h-10 w-auto"
                />
            </div>
        </nav>
    )
}

const EmployeeProfile: React.FC = () => {
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState<TabType>('Personal')
    const [showSuccessPage, setShowSuccessPage] = useState(false)
    const [isRegulationsAccepted, setIsRegulationsAccepted] = useState(false)

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEE, token],
        queryFn: () => getEmployeeInfoByTokenConfirm(token || ''),
        loadingComponent: (
            <div className="flex justify-center items-center min-h-screen">
                <Loading.CircularProgress width={40} height={40} />
            </div>
        ),
    })

    const tabs: Array<{ key: TabType; label: string }> = [
        { key: 'Personal', label: t('employees.personal') },
        { key: 'Identification', label: t('employees.identification') },
        { key: 'Address', label: t('employees.address') },
        { key: 'Finance', label: t('employees.finance') },
        { key: 'Experience', label: t('employees.experience') },
        { key: 'Profile', label: t('employees.profile') },
    ]

    const {
        mutateAsync: employeeConfirmMutation,
        isPending: isConfirmPending,
    } = useMutationWithHandler(
        {
            mutationFn: employeeConfirm,
        },
        {
            reactQueryKeys: QUERY_KEYS.EMPLOYEES,
            successMessage: 'employee-confirmation-success',
            errorMessage: 'error-occurred',
            onSuccess: () => {
                setShowSuccessPage(true)
            },
        }
    )

    const { isUploading, uploadAsync } = useUploadFile({
        prefix: 'employees/documents',
    })

    const formControl = useForm<EmployeeConfimationValues>({
        defaultValues: useMemo(
            () =>
                data?.result
                    ? {
                          ...defaultValues,
                          // Personal Tab Fields
                          employee_name:
                              data?.result?.employee_name ||
                              defaultValues.employee_name ||
                              '',
                          employee_code:
                              data?.result?.employee_code ||
                              defaultValues.employee_code ||
                              '',
                          department_id:
                              data?.result?.department_id ||
                              defaultValues.department_id ||
                              0,
                          position_id:
                              data?.result?.position_id ||
                              defaultValues.position_id ||
                              0,

                          temporary_same_as_permanent: false,
                      }
                    : defaultValues,
            [data?.result]
        ),
        mode: 'onChange',
        resolver: yupResolver(
            validateStep({ activeTab, requireEmergencyContact: true }) as any
        ),
    })

    // Reset form when data is loaded to ensure ProfileHeader shows employee info
    useEffect(() => {
        if (data?.result) {
            formControl.reset({
                ...defaultValues,
                ...data.result,
                // contact_homies từ DB có thể là {} (default JSONB) → ép về mảng
                // để các field contact_homies.0.* bind đúng.
                contact_homies:
                    Array.isArray(data.result.contact_homies) &&
                    data.result.contact_homies.length > 0
                        ? data.result.contact_homies
                        : defaultValues.contact_homies,
                temporary_same_as_permanent: false,
            })
        }
    }, [data?.result])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const handleNext = () => {
        const currentIndex = tabs.findIndex((tab) => tab.key === activeTab)
        if (currentIndex < tabs.length - 1) {
            setActiveTab(tabs[currentIndex + 1].key)
        }
        if (currentIndex === tabs.length - 1) {
            formControl.handleSubmit(onSubmit as any)()
        }
    }

    const handlePrevious = () => {
        const currentIndex = tabs.findIndex((tab) => tab.key === activeTab)
        if (currentIndex > 0) {
            setActiveTab(tabs[currentIndex - 1].key)
        }
    }

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'Personal':
                return (
                    <PersonalTab
                        formControl={formControl}
                        onNext={handleNext}
                    />
                )
            case 'Identification':
                return (
                    <IdentificationTab
                        formControl={formControl}
                        onNext={handleNext}
                        onPrevious={handlePrevious}
                    />
                )
            case 'Address':
                return (
                    <AddressTab
                        onNext={handleNext}
                        onPrevious={handlePrevious}
                        formControl={formControl}
                    />
                )
            case 'Finance':
                return (
                    <FinanceTab
                        onNext={handleNext}
                        onPrevious={handlePrevious}
                        formControl={formControl}
                    />
                )
            case 'Experience':
                return (
                    <ExperienceTab
                        formControl={formControl}
                        onNext={handleNext}
                        onPrevious={handlePrevious}
                    />
                )
            case 'Profile':
                return (
                    <ProfileTab
                        onNext={handleNext}
                        onPrevious={handlePrevious}
                        formControl={formControl}
                        isPending={isConfirmPending || isUploading}
                    />
                )
            default:
                return <PersonalTab formControl={formControl} />
        }
    }

    const handleFinishOnboarding = () => {
        // Could redirect to login page or dashboard
        window.location.href = '/login'
    }

    const onSubmit = async (values: EmployeeConfimationValues) => {
        console.log('Form values:', values)

        // Handle document uploads
        const documentsWithAttachments = []

        if (values?.documents) {
            for (const document of values.documents) {
                if (document.attachments && document.attachments.length > 0) {
                    // Upload attachments for this document type
                    const attachmentUploadeds = await uploadAsync(
                        document.attachments
                    )

                    const attachments = attachmentUploadeds
                        ?.filter(
                            (attachment) => attachment.status === 'fulfilled'
                        )
                        .map((attachment) => attachment.value) as Attachment[]

                    documentsWithAttachments.push({
                        document_type: document.document_type,
                        attachments: attachments,
                    })
                } else {
                    // Keep document without attachments
                    documentsWithAttachments.push({
                        document_type: document.document_type,
                        attachments: [],
                    })
                }
            }
        }

        // Prepare final form data
        const finalFormData = {
            ...values,
            documents: documentsWithAttachments,
        }

        console.log('Final form data with uploaded attachments:', finalFormData)

        await employeeConfirmMutation({
            token: token || '',
            params_employee: {
                ...finalFormData,
                employee_birthday: values?.employee_birthday
                    ? formatPGDate(values?.employee_birthday, 'YYYY-MM-DD')
                    : null,
                employee_idno_issued_on: values?.employee_idno_issued_on
                    ? formatPGDate(
                          values?.employee_idno_issued_on,
                          'YYYY-MM-DDTHH:mm:ss'
                      )
                    : null,
            } as any,
        })
    }

    // Show success page if submission was successful
    if (showSuccessPage) {
        return (
            <OnboardingSuccess
                employeeName={data?.result?.employee_name || 'Bạn'}
                onFinish={handleFinishOnboarding}
            />
        )
    }

    const shouldShowRegulationsGate = Boolean(token) && !isRegulationsAccepted

    return (
        <>
            {shouldShowRegulationsGate && (
                <RegulationsGate
                    token={token || ''}
                    onAccepted={() => setIsRegulationsAccepted(true)}
                />
            )}
            <EmployeeConfirmationNavbar />
            <div className="min-h-screen bg-gray-100 pt-20 md:pt-16">
                <div className="max-w-7xl mx-auto p-2 md:p-4">
                    {/* Profile Header */}
                    <ProfileHeader
                        formControl={formControl}
                        employee={data?.result!}
                    />

                    <div className="bg-white rounded-md shadow-sm border border-gray-200 p-2 md:p-4">
                        <div className="border-b border-gray-200 overflow-x-auto scrollbar-hide -mx-2 md:mx-0 px-2 md:px-0">
                            <nav className="flex space-x-2 md:space-x-8 px-2 md:px-6 md:justify-center w-full min-w-max md:min-w-0">
                                {tabs.map((tab, index) => {
                                    const currentIndex = tabs.findIndex(
                                        (t) => t.key === activeTab
                                    )
                                    const isCompleted = index < currentIndex
                                    const isActive = tab.key === activeTab
                                    const isAccessible = index <= currentIndex // Can only access current or previous steps

                                    return (
                                        <button
                                            key={tab.key}
                                            onClick={() =>
                                                isAccessible &&
                                                setActiveTab(tab.key)
                                            }
                                            disabled={!isAccessible}
                                            className={`py-3 md:py-4 px-2 md:px-1 border-b-2 capitalize font-medium text-xs md:text-sm transition-colors relative whitespace-nowrap ${
                                                isActive
                                                    ? 'border-blue-500 text-blue-600'
                                                    : isCompleted
                                                      ? 'border-green-400 text-green-600 hover:text-green-700'
                                                      : isAccessible
                                                        ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                        : 'border-transparent text-gray-300 cursor-not-allowed'
                                            }`}
                                        >
                                            {isCompleted && (
                                                <span className="absolute -top-1 -right-1 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                                                    <CheckIcons className="w-4 h-4 text-success" />
                                                </span>
                                            )}
                                            {tab.label}
                                        </button>
                                    )
                                })}
                            </nav>
                        </div>

                        {/* Tab Content */}
                        <div className="p-3 md:p-6">{renderActiveTab()}</div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default EmployeeProfile
